<template>
  <div
    class="wrn-accordion"
    :class="{ opened: isOpen, 'wrn-accordion--disabled': disabled }"
  >
    <button
      :class="{ 'wrn-link': !disabled, 'wrn-accordion-button': true }"
      @click="accordionChanged"
    >
      <slot name="title">
        <BaseText
          size="lg"
          class="wrn-p-y-1"
          :color="NebraskaColors.elementPrimary"
          >{{ title }}</BaseText
        >
      </slot>
      <section class="wrn-accordion__actions">
        <slot name="subtitle">
          <BaseText v-show="subtitle" size="lg" class="wrn-p-y-1">
            {{ subtitle }}
          </BaseText>
        </slot>
        <BaseIcon
          icon="ic_chevron_down"
          class="icon wrn-m-l-05 wrn-accordion__actions__icon"
          width="24px"
          height="24px"
          :colors="iconColor"
        />
      </section>
    </button>
    <div v-show="isOpen" :class="shouldAddTextStyle && computedTextClass">
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Emit, Watch } from 'vue-property-decorator';
import { BaseIcon, BaseText } from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

@Component({
  components: { BaseIcon, BaseText }
})
export default class Accordion extends Vue {
  @Prop({ type: String, required: false })
  public subtitle?: string;

  @Prop({ type: String, required: false })
  public title?: string;

  @Prop({ type: Boolean, default: false })
  public open?: boolean;

  @Prop({ type: Boolean, default: false })
  public isPrimary!: boolean;

  @Prop({ type: Boolean, default: false })
  public disabled!: boolean;

  @Prop({ type: Boolean, default: true })
  public shouldAddTextStyle!: boolean;

  private isOpen = false;
  private NebraskaColors = NebraskaColors;

  private get computedTextClass() {
    if (this.isPrimary) {
      return 'text-p-3 wrn-p-b-1 wrn-accordion-text_primary';
    } else {
      return 'text-p-3 wrn-p-b-1 wrn-accordion-text';
    }
  }

  @Watch('open', { immediate: true })
  private changeStatus(value: boolean) {
    this.isOpen = value;
  }

  @Emit()
  private accordionChanged() {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;

    return this.isOpen;
  }

  get iconColor() {
    return this.disabled
      ? NebraskaColors.elementDisabled
      : NebraskaColors.elementPrimary;
  }
}
</script>

<style lang="less" scoped>
.wrn-accordion {
  display: block;
  border-top: 1px solid @divider-primary;

  &--disabled {
    .wrn-accordion-button {
      cursor: default !important;
    }
  }

  &__actions {
    display: flex;

    &__icon {
      align-self: center;
    }
  }

  .wrn-accordion-text,
  .wrn-accordion-text /deep/ p {
    color: @text-secondary;
    margin-bottom: @size-spacing-x500;
    :last-child {
      margin-bottom: 0;
    }
  }

  .wrn-accordion-text_primary,
  .wrn-accordion-text_primary /deep/ p {
    color: @text-primary;
    line-height: 28px;
    margin-bottom: @size-spacing-x500;
    :last-child {
      margin-bottom: 0;
    }
    a {
      text-decoration: underline;
    }
  }

  .wrn-accordion-text_primary,
  .wrn-accordion-text_primary /deep/ ul li {
    list-style: inside;
  }

  .wrn-accordion-button {
    background: transparent;
    border: 0;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    text-align: left;
    width: 100%;

    > div {
      width: 100%;
      padding-right: 8px;
    }
  }

  .icon {
    transition: transform 200ms cubic-bezier(0.55, 0, 0.1, 1);
  }

  &.opened {
    .icon {
      transform: rotate(180deg);
    }
  }
}
</style>
